import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Building2, Plus, RefreshCw, Database } from 'lucide-react';
import ERPAdvancedGrid    from './erp/ERPAdvancedGrid';
import useERPContextMenu  from './erp/ERPContextMenu';
import { erpToast }       from './erp/ERPToast';
import '../styles/erp-theme.css';
import { API, apiFetch } from '../lib/api';

const COLUMNS = [
  { field: 'IndustryCode', header: 'Industry Code', visible: true, frozen: true },
  { field: 'IndustryName', header: 'Industry Name', visible: true },
  { field: 'Category',     header: 'Category',      visible: true },
  { field: 'Icon',         header: 'Icon',          visible: true },
  { field: 'TemplateDB',   header: 'Template DB',   visible: true },
  { field: 'SortOrder',    header: 'Sort',          visible: true },
  { field: 'IsActive',     header: 'Active',        visible: true },
  { field: 'CreatedAt',    header: 'Created',       visible: false },
  { field: 'UpdatedAt',    header: 'Updated',       visible: false }
];

/**
 * IndustryMasterManager — full CRUD over WebApp.tbl_industry_master.
 * Mirrors UAMSMasterManager pattern: ERPAdvancedGrid + context menu + modal.
 */
export default function IndustryMasterManager() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('SortOrder');
  const [sortDir, setSortDir]     = useState('asc');
  const [filters, setFilters]     = useState({});

  const [editing, setEditing] = useState(null); // { mode, row }

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`${API}/uams/industries?activeOnly=false`)
      .then(r => r.json())
      .then(d => {
        if (d?.error) setError(d.error);
        else { setRows(Array.isArray(d) ? d : []); setError(null); }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    let r = rows;
    const af = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (af.length) {
      r = r.filter(row => af.every(([k, v]) => String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    }
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const total = filteredSorted.length;
  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);

  const onSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };

  const startAdd       = useCallback(() => setEditing({ mode: 'add', row: null }), []);
  const startEdit      = useCallback((row) => setEditing({ mode: 'edit', row }), []);
  const startView      = useCallback((row) => setEditing({ mode: 'view', row }), []);
  const startDuplicate = useCallback((row) => setEditing({ mode: 'duplicate', row }), []);

  const onDelete = useCallback(async (row) => {
    const ic = row.IndustryCode;
    const choice = window.confirm(`Soft-delete (deactivate) industry "${row.IndustryName || ic}"?\n\nPress Cancel to keep, OK to deactivate.\nFor permanent deletion, use the context menu → Hard Delete.`);
    if (!choice) return;
    try {
      const res = await apiFetch(`${API}/uams/industries/${encodeURIComponent(ic)}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`Delete failed: ${data.error || res.status}`);
      else { erpToast.success('Industry deactivated'); load(); }
    } catch (e) { erpToast.error(`Delete failed: ${e.message}`); }
  }, [load]);

  const onHardDelete = useCallback(async (row) => {
    const ic = row.IndustryCode;
    if (!window.confirm(`HARD DELETE industry "${row.IndustryName || ic}"?\nThis removes the master row AND all matrix junction rows. Cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API}/uams/industries/${encodeURIComponent(ic)}?hard=true`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`Hard-delete failed: ${data.error || res.status}`);
      else { erpToast.success('Industry permanently deleted'); load(); }
    } catch (e) { erpToast.error(`Hard-delete failed: ${e.message}`); }
  }, [load]);

  const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
    idField: 'IndustryCode',
    copyKeys: [{ field: 'IndustryCode', label: 'Copy Industry Code' }],
    onView:    startView,
    onEdit:    startEdit,
    onPreview: (r) => {
      const lines = Object.entries(r).map(([k, v]) => `${k}: ${v ?? ''}`).join('\n');
      const w = window.open('', '_blank');
      w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px;white-space:pre-wrap">${lines.replace(/</g, '&lt;')}</pre>`);
    },
    onPrint:   () => window.print(),
    onExport:  (r) => {
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${r.IndustryCode || 'industry'}.json`; a.click();
    },
    extraItems: [
      { label: 'Duplicate',   onClick: startDuplicate, testId: 'ctx-duplicate' },
      { label: 'Deactivate',  onClick: onDelete,       testId: 'ctx-deactivate' },
      { label: 'Hard Delete', onClick: onHardDelete,   testId: 'ctx-hard-delete', danger: true }
    ]
  });

  return (
    <div data-testid="industry-master-manager" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--erp-info-bg, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 11, color: '#1e40af'
      }}>
        <Database size={14} />
        <div>
          <strong>Industry Master</strong> · Each new industry auto-seeds a default <code>POOL_&lt;CODE&gt;</code> pool entry and a per-industry TemplateDB. Edits propagate to the Industry × Tier matrix and signup metadata immediately.
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 11 }}>
          {error} <button onClick={load} className="erp-btn" style={{ marginLeft: 8 }}><RefreshCw size={11} /> Retry</button>
        </div>
      )}

      <ERPAdvancedGrid
        title={`Industry Master (${total})`}
        columns={COLUMNS}
        data={paginated}
        total={total}
        idField="IndustryCode"
        nameField="IndustryName"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        showAddNew={true}
        onAddNew={startAdd}
        onEdit={(row, mode) => mode === 'view' ? startView(row) : startEdit(row)}
        onPreview={startView}
        testId="industry-master"
      />

      <RowContextDelegate
        gridTestId="industry-master"
        rows={paginated}
        idField="IndustryCode"
        onRowContextMenu={onRowContextMenu}
      />
      {ContextMenuPortal}

      {editing && (
        <IndustryFormModal
          mode={editing.mode}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function IndustryFormModal({ mode, row, onClose, onSaved }) {
  const isView = mode === 'view';
  const isAdd  = mode === 'add' || mode === 'duplicate';

  const initial = useMemo(() => {
    if (row) {
      const base = {
        industryCode: row.IndustryCode || '',
        industryName: row.IndustryName || '',
        category:     row.Category || '',
        icon:         row.Icon || '',
        sortOrder:    row.SortOrder ?? '',
        isActive:     row.IsActive === true || row.IsActive === 1,
        templateDB:   row.TemplateDB || ''
      };
      if (mode === 'duplicate') {
        base.industryCode = '';
        base.industryName = (base.industryName || '') + ' (copy)';
      }
      return base;
    }
    return { industryCode: '', industryName: '', category: '', icon: '', sortOrder: 999, isActive: true, templateDB: '' };
  }, [row, mode]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);

  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (isView) { onClose(); return; }
    setSaving(true);
    try {
      const payload = {
        industryCode: form.industryCode,
        industryName: form.industryName,
        category:     form.category || null,
        icon:         form.icon || null,
        sortOrder:    form.sortOrder === '' ? 999 : parseInt(form.sortOrder, 10),
        isActive:     !!form.isActive,
        templateDB:   form.templateDB || null
      };
      let res;
      if (isAdd) {
        res = await apiFetch(`${API}/uams/industries`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        const ic = row.IndustryCode;
        const { industryCode, ...rest } = payload;
        res = await apiFetch(`${API}/uams/industries/${encodeURIComponent(ic)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(rest)
        });
      }
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`${isAdd ? 'Create' : 'Update'} failed: ${data.error || res.status}`);
      else { erpToast.success(`Industry ${isAdd ? 'created' : 'updated'}`); onSaved(); }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
      onClick={onClose}
      data-testid="industry-master-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-surface, #fff)', color: 'var(--text-primary, #0f172a)', borderRadius: 10, padding: 20, minWidth: 520, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 30px 60px rgba(15,23,42,0.4)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Building2 size={18} /> {isAdd ? 'Add Industry' : isView ? 'View Industry' : 'Edit Industry'}
          </h3>
          <button type="button" onClick={onClose} className="erp-btn" style={{ padding: '4px 10px' }} data-testid="industry-master-cancel">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormField label="Industry Code" required>
            <input className="form-input" value={form.industryCode}
              onChange={(e) => setF('industryCode', e.target.value.toUpperCase())}
              disabled={isView || (mode === 'edit')} required
              placeholder="e.g. RETAIL"
              data-testid="industry-form-code" />
          </FormField>
          <FormField label="Industry Name" required>
            <input className="form-input" value={form.industryName}
              onChange={(e) => setF('industryName', e.target.value)}
              disabled={isView} required
              data-testid="industry-form-name" />
          </FormField>
          <FormField label="Category">
            <input className="form-input" value={form.category}
              onChange={(e) => setF('category', e.target.value)}
              disabled={isView}
              placeholder="e.g. Fashion, Food, Services"
              data-testid="industry-form-category" />
          </FormField>
          <FormField label="Icon">
            <input className="form-input" value={form.icon}
              onChange={(e) => setF('icon', e.target.value)}
              disabled={isView}
              placeholder="emoji or lucide-icon name"
              data-testid="industry-form-icon" />
          </FormField>
          <FormField label="Sort Order">
            <input className="form-input" type="number" value={form.sortOrder}
              onChange={(e) => setF('sortOrder', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="industry-form-sort" />
          </FormField>
          <FormField label="Active">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <input type="checkbox" checked={!!form.isActive}
                onChange={(e) => setF('isActive', e.target.checked)}
                disabled={isView}
                data-testid="industry-form-active" />
              <span style={{ fontSize: 12 }}>{form.isActive ? 'Active' : 'Inactive'}</span>
            </label>
          </FormField>
          <FormField label="Template DB" span={2}>
            <input className="form-input" value={form.templateDB}
              onChange={(e) => setF('templateDB', e.target.value)}
              disabled={isView}
              placeholder="Auto: RC26ERP_TEMPLATE_<CODE> if blank"
              data-testid="industry-form-templatedb" />
          </FormField>
        </div>

        {!isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn" disabled={saving} data-testid="industry-form-cancel-btn">Cancel</button>
            <button type="submit" className="erp-btn erp-btn-primary" disabled={saving} data-testid="industry-form-save">
              {saving ? 'Saving…' : (isAdd ? <><Plus size={12} /> Create</> : 'Save')}
            </button>
          </div>
        )}
        {isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn erp-btn-primary" data-testid="industry-form-close-view">Close</button>
          </div>
        )}
      </form>
    </div>
  );
}

function FormField({ label, required, span = 1, children }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function RowContextDelegate({ gridTestId, rows, idField, onRowContextMenu }) {
  const ref = useRef({ rows, idField });
  ref.current = { rows, idField };
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest(`tr[data-testid^="${gridTestId}-row-"]`);
      if (!tr) return;
      const idAttr = tr.getAttribute('data-testid');
      const id = idAttr.replace(`${gridTestId}-row-`, '');
      const row = ref.current.rows.find(r => String(r[ref.current.idField]) === id);
      if (row) onRowContextMenu(e, row);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [gridTestId, onRowContextMenu]);
  return null;
}
